import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import MissionDetailCards from '../MissionComponents/MissionDetailCards';

import { createMission, updateDuty } from '../../utils/api';

const MissionDetails = (props) => {
    const routerNavigate = useNavigate();
    const navigate = props.navigate || routerNavigate;
    const [missions, setMissions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addMission = () => {
        const newId = missions.length > 0 ? Math.max(...missions.map(m => m.id)) + 1 : 1;
        setMissions([...missions, { id: newId, missionId: '', poiVisible: '', poiNotVisible: '', poiAdded: '', status: 'In Progress' }]);
    };

    const removeMission = (id) => {
        setMissions(missions.filter(m => m.id !== id));
    };

    const handleInputChange = (id, field, value) => {
        setMissions(missions.map(m => m.id === id ? { ...m, [field]: value } : m));
    };

    const getISTTimeDisplay = () => {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(now.getTime() + istOffset);
        return istDate.toISOString().replace("T", " ").slice(0, 16);
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;

        if (missions.length === 0) {
            toast.error('Please add at least one mission before submitting.');
            return;
        }

        // Validate all fields in each mission
        const isAllValid = missions.every(m =>
            m.missionId?.trim() &&
            m.poiVisible !== '' &&
            m.poiNotVisible !== '' &&
            m.poiAdded !== ''
        );

        if (!isAllValid) {
            toast.error('Please fill in all fields for all missions.');
            return;
        }

        // Get active duty ID from localStorage
        const activeDutyId = localStorage.getItem('active_duty_id');
        if (!activeDutyId) {
            toast.error('No active duty found. Please start a mission first.');
            return;
        }

        setIsSubmitting(true);
        try {
            // Loop through all missions and submit each one
            const submissionPromises = missions.map(async (mission) => {
                const missionData = {
                    missionId: mission.missionId,
                    dutyId: activeDutyId,
                    valid: mission.poiVisible,
                    invalid: mission.poiNotVisible,
                    created: mission.poiAdded,
                    status: mission.status
                };
                return await createMission(missionData);
            });
            // Wait for all missions to be submitted
            await Promise.all(submissionPromises);
            // Update duty with stop time
            await updateDuty(activeDutyId, {
                stop_time: getISTTimeDisplay()
            });

            // Clear mission status and show success
            localStorage.removeItem('mission_status');
            localStorage.setItem('qc_pending', 'true');

            toast.success("Missions submitted successfully!");
            // Notify parent to proceed to QC
            if (props.onMissionsSubmitted) {
                props.onMissionsSubmitted();
            }
        } catch (error) {
            console.error('Error submitting missions:', error);
            toast.error('Failed to submit missions: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitting) {
        // Optional: can show a loading overlay here if needed, but the button handles loading text nicely
    }

    return (
        <div className="min-h-[100dvh] bg-white flex flex-col font-sans">
            {/* Main Content Area */}
            <div className="flex-1 overflow-y-auto">
                <div className="max-w-md mx-auto px-10 pt-16 pb-48 relative">

                    <div className="mb-8">
                        <h1 className="text-[28px] font-bold text-gray-900 leading-tight">Mission Details</h1>
                        <p className="text-base text-gray-400 mt-1 font-normal">Add and manage your missions</p>
                    </div>

                    <div className="space-y-6">
                        {missions.map((mission, index) => (
                            <MissionDetailCards
                                key={mission.id}
                                mission={mission}
                                index={index}
                                onRemove={removeMission}
                                onInputChange={handleInputChange}
                            />
                        ))}

                        {/* + Add Mission Button - Smaller and Refined */}
                        <div className="flex justify-center pt-2">
                            <button
                                onClick={addMission}
                                className={`${missions.length > 0 ? 'w-auto px-8' : 'w-full max-w-[260px]'} bg-[#0084FF] hover:bg-[#0077e6] text-white font-medium py-2.5 rounded-xl shadow-sm transition-all transform active:scale-95 text-sm flex items-center justify-center whitespace-nowrap`}>
                                + Add Mission
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Bottom Actions */}
            <div className="fixed bottom-0 left-0 right-0 z-50">
                {/* Submit Button Area with thin top border */}
                <div className="bg-white px-10 py-4 border-t border-gray-100 flex justify-center">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="w-full max-w-[240px] bg-[#00BFA5] hover:bg-[#00a68f] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2.5 px-6 rounded-xl shadow-sm transition transform active:scale-95 text-sm uppercase tracking-wide"
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Missions'}
                    </button>
                </div>

                {/* Bottom Navigation */}
                <nav className="bg-white border-t border-gray-100">
                    <div className="flex items-center justify-center gap-x-20 py-4 max-w-md mx-auto">
                        <button onClick={() => navigate('/collectors-home')} className="flex flex-col items-center space-y-1 text-[#00BFA5]">
                            <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                            </svg>
                            <span className="text-[11px] font-medium">Home</span>
                        </button>
                        <button
                            onClick={() => navigate('/logs')}
                            className="flex flex-col items-center space-y-1 text-gray-400"
                        >
                            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-[11px] font-medium">Logs</span>
                        </button>
                    </div>
                </nav>
            </div>
        </div>
    );
};

export default MissionDetails;